import axios from 'axios';
import { getAuthToken, getRefreshToken, saveAuthSession, clearAuthSession } from '../utils/auth';

/**
 * Main axios instance.
 * - Injects Authorization: Bearer header on every request
 * - On 401: attempts token refresh (deduplicated), retries original request
 * - On refresh failure: clears session and redirects to /login
 * - Normalizes error.friendlyMessage from server response
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  // NO default Content-Type. Axios already sets application/json for a plain
  // object body, and leaving the header unset is what lets the browser stamp
  // `multipart/form-data; boundary=...` on a FormData body. Setting it here
  // breaks EVERY upload in the app: axios v1 sees a JSON content-type on a
  // FormData and runs formDataToJSON(), so the file silently becomes the
  // string "undefined" in a JSON payload and Laravel rejects it with
  // "The proof field must be a file."
  // Architecture Rule 73 — send the `device_id` cookie on every authenticated
  // request so the Authorized-Device gate recognises a registered browser.
  // Requires backend CORS supports_credentials=true.
  withCredentials: true,
});

// ── Request interceptor ───────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Architecture Rule 28: send X-Request-Id (the backend honors it for log correlation)
    // and keep X-Client-Request-Id for backwards-compat / debugging.
    const requestId = crypto.randomUUID();
    config.headers['X-Request-Id'] = requestId;
    config.headers['X-Client-Request-Id'] = requestId;
    config._requestId = requestId;
    config._startTime = Date.now();
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor: stash last request_id for ErrorBoundary ─────────────
api.interceptors.response.use(
  (response) => {
    const id = response?.headers?.['x-request-id'] ?? response?.config?._requestId;
    if (id) window.__lastRequestId = id;
    return response;
  },
  (error) => {
    const id = error?.response?.headers?.['x-request-id'] ?? error?.config?._requestId;
    if (id) window.__lastRequestId = id;
    return Promise.reject(error);
  },
);

// ── Token refresh (deduplicated) ──────────────────────────────────────────────
let _refreshPromise = null;

async function doRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const { auth, user, session } = response.data.data;
  saveAuthSession(auth, user, session);
  return auth.access_token;
}

// ── Response interceptor ──────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry && getRefreshToken()) {
      original._retry = true;

      if (!_refreshPromise) {
        _refreshPromise = doRefresh().finally(() => {
          _refreshPromise = null;
        });
      }

      try {
        const newToken = await _refreshPromise;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        clearAuthSession();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // Normalize friendly error message
    error.friendlyMessage =
      error.response?.data?.message ?? 'An unexpected error occurred. Please try again.';

    // The per-field reasons, normalized to { field: [message, ...] }.
    //
    // A 422 from this API already carries the specific reasons — the password
    // policy returns "must be at least 12 characters", "must include 3 of ..."
    // — but every caller was reading only `friendlyMessage`, so the user saw
    // the useless summary ("Password does not meet policy.") while the actual
    // answer sat unread in the same response. Normalizing it here means a form
    // never has to reach into error.response.data itself.
    const raw = error.response?.data?.errors;
    error.fieldErrors = raw && typeof raw === 'object' && !Array.isArray(raw)
      ? Object.fromEntries(
        Object.entries(raw).map(([field, messages]) => [
          field,
          Array.isArray(messages) ? messages.map(String) : [String(messages)],
        ]),
      )
      : {};

    return Promise.reject(error);
  },
);

export default api;
